import moment from 'moment-timezone';
import userHelper from 'helpers/userHelper';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

export const formatUsdCents = cents =>
  Number.isFinite(Number(cents)) ? usd.format(Number(cents) / 100) : '—';

// Billing dates are stored in UTC and shown in the user's profile timezone,
// matching the notary app. The store keeps this in sync with userMaster.
let billingTimezone = null;

export const setBillingTimezone = timezone => {
  billingTimezone = userHelper.getTimezoneVal(timezone);
};

export const toBillingMoment = value => {
  const utc = moment.utc(value);
  return billingTimezone ? utc.tz(billingTimezone) : utc.local();
};

// Start of the picked calendar day in the billing timezone, as a UTC instant
// for API filters (empty when no date is picked).
export const billingDayStartUtc = dateText => {
  if (!dateText) return '';
  const day = billingTimezone
    ? moment.tz(dateText, 'YYYY-MM-DD', billingTimezone)
    : moment(dateText, 'YYYY-MM-DD');
  return day.isValid() ? day.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : '';
};

export const formatBillingDate = (value, format = 'MMM D, YYYY, h:mm A') =>
  value ? toBillingMoment(value).format(format) : '—';

// Short zone name (for example "CST") of the timezone dates are shown in.
export const billingTimezoneLabel = () =>
  toBillingMoment(moment.utc()).format('z') ||
  toBillingMoment(moment.utc()).format('[UTC]Z');

const ISO_RANGE =
  /\[(\d{4}-\d{2}-\d{2}T[^,\])\s]+),\s*(\d{4}-\d{2}-\d{2}T[^,\])\s]+)[\])]/;

// Backend descriptions embed the period as raw ISO timestamps. Replace them
// with a readable range in the user's timezone.
export const formatInvoiceDescription = description => {
  const text = String(description ?? '');
  const match = ISO_RANGE.exec(text);
  if (!match) return text;
  const start = toBillingMoment(match[1]);
  const end = toBillingMoment(match[2]);
  if (!start.isValid() || !end.isValid()) return text;
  const startFormat = start.year() === end.year() ? 'MMM D' : 'MMM D, YYYY';
  const range = `${start.format(startFormat)} – ${end.format('MMM D, YYYY')}`;
  return text.replace(ISO_RANGE, `(${range})`);
};

export const formatSubscriptionLastCharge = value => {
  const charge = moment.utc(value);
  if (!value || !charge.isValid() || charge.valueOf() <= 0) {
    return 'No charge recorded';
  }
  return formatBillingDate(value, 'MMMM Do YYYY, h:mm a');
};

export const formatBillingCycleRange = (startUtc, endUtc) => {
  const start = toBillingMoment(startUtc);
  const end = toBillingMoment(endUtc);
  if (!startUtc || !endUtc || !start.isValid() || !end.isValid()) {
    return 'Not started yet';
  }
  const startFormat = start.year() === end.year() ? 'MMM D' : 'MMM D, YYYY';
  return `${start.format(startFormat)} – ${end.format('MMM D, YYYY')}`;
};

export const sponsorshipNotice = payer => {
  if (!payer) return null;
  const organization = payer.OrganizationName || 'Your organization';
  const cadence =
    payer.Frequency === 'Annual'
      ? 'yearly'
      : payer.Frequency === 'Monthly'
      ? 'monthly'
      : null;
  const cadenceText = cadence ? ` ${cadence}` : '';
  if (payer.CurrentPayer === 'Organization') {
    return `${organization} pays for this subscription${cadenceText}. Individual payment changes are unavailable.`;
  }
  if (payer.NextPayer === 'Organization') {
    return `Your current plan continues through ${formatBillingDate(
      payer.EffectiveFromUtc
    )}. After that, ${organization} pays${cadenceText}, and you are not charged for that period. Plan changes and cancellation stay unavailable while this sponsorship is scheduled.`;
  }
  return null;
};

export const sponsorshipReasonLabel = (reason, qualifies, quantity) => {
  const volume = Boolean(qualifies) && Number(quantity) >= 2;
  if (reason === 'sharing-enabled' || reason === 'sponsored-qualifying') {
    return volume ? 'Sharing' : 'Sharing, lower rate at 2';
  }
  if (reason === 'sharing-disabled' || reason === 'sponsored-not-sharing') {
    return 'Not sharing';
  }
  if (reason === 'no-hosted-data') {
    return 'No hosted data';
  }
  if (reason === 'connection-disconnected') {
    return 'Disconnected';
  }
  if (reason === 'hosted-access-revoked') {
    return 'Access ended';
  }
  return reason || '—';
};

export const pricingReasonLabel = line => {
  if (!line) return '—';
  const quantity = Number(line.Quantity);
  const accounts = Number.isFinite(quantity)
    ? `${quantity} × ${formatUsdCents(line.UnitAmountCents)}`
    : '';
  const withQuantity = text => (accounts ? `${text}, ${accounts}` : text);
  if (line.RequiresPricingReview || line.Reason === 'price-not-configured') {
    return 'Pricing review required';
  }
  switch (line.Reason) {
    case 'custom-price':
      return withQuantity(
        line.Reference ? `Custom rate (${line.Reference})` : 'Custom rate'
      );
    case 'standard-price':
      return withQuantity('Standard rate');
    case 'sponsored-qualifying':
      return withQuantity('Group rate, notary shares data');
    case 'sponsored-not-sharing':
      return withQuantity('Standard rate, notary not sharing data');
    case 'grace-deferred':
      return 'Grace period, billed next cycle';
    case 'quantity-zero':
      return 'No billable accounts';
    default:
      return line.Explanation || line.Reason || '—';
  }
};

export const isOrganizationSponsored = payer =>
  payer?.CurrentPayer === 'Organization' || payer?.NextPayer === 'Organization';
